import type { Passage } from "./types";

export const passage: Passage = {
  slug: "psalm-46-10",
  reference: "Psalm 46:10",
  title: "Be still and know: what Psalm 46:10 is actually saying",
  h1: "Psalm 46:10, and the war it is spoken into",
  description:
    "Be still and know that I am God is read as an invitation to quiet. In the psalm it is an order to stop fighting, addressed to nations, in the middle of a war.",
  targets: [
    "be still and know that i am god meaning",
    "psalm 46 10 explained",
    "what does be still and know mean",
  ],
  published: "2026-09-03",
  standfirst:
    "It is printed on candles and cushions as an invitation to slow down. The psalm it comes from is about the earth giving way and the nations in uproar.",
  verse: {
    text: "Be still, and know that I am God. I will be exalted among the nations. I will be exalted in the earth.",
    ref: "Psalm 46:10",
  },
  context: [
    {
      text: "God is our refuge and strength, a very present help in trouble. Therefore we won't be afraid, though the earth changes, though the mountains are shaken into the heart of the seas; though its waters roar and are troubled, though the mountains tremble with their swelling.",
      ref: "Psalm 46:1–3",
    },
    {
      text: "He makes wars cease to the end of the earth. He breaks the bow, and shatters the spear. He burns the chariots in the fire.",
      ref: "Psalm 46:9",
    },
  ],
  takeaways: [
    "The Hebrew verb is closer to let go, cease, or drop your hands than to be quiet. It is an order to stop doing something, not an invitation to feel calm.",
    "The addressee in the psalm is the nations, not the individual reader. Verse 9 has just described God ending wars by destroying the weapons.",
    "The stillness is a consequence of the disarmament, not a mood to be produced beforehand.",
    "None of which makes it useless in a quiet time. It just means the comfort on offer is that someone else is holding the situation, rather than that you should relax.",
  ],
  body: [
    {
      type: "h2",
      id: "the-psalm-it-comes-from",
      text: "The psalm it comes from",
    },
    {
      type: "p",
      text: "Psalm 46 is not a quiet psalm. It opens with the earth changing, mountains falling into the sea and waters roaring, and it moves from there to nations in uproar and kingdoms tottering. By verse 9 God is breaking bows, shattering spears and burning chariots.",
    },
    {
      type: "p",
      text: "Verse 10 arrives immediately after that. Whatever it means, it is spoken over a battlefield rather than a candlelit room, and the sentence that follows it is about being exalted among the nations rather than about anybody's inner life.",
    },
    {
      type: "h2",
      id: "the-verb",
      text: "The verb is not be quiet",
    },
    {
      type: "p",
      text: "The Hebrew is a form of a root meaning to sink, slacken, let drop, or withdraw. Elsewhere it is used for hands going limp and for leaving something alone. Several translations render it enough! or stop!, and that is closer to the force of it than be still.",
    },
    {
      type: "p",
      text: "So the instruction is not primarily about noise. It is about ceasing an activity — and in a psalm whose previous verse has just described weapons being destroyed, the activity being stopped is not chatter.",
    },
    {
      type: "h2",
      id: "who-is-being-addressed",
      text: "Who is being told to stop",
    },
    {
      type: "p",
      text: "The verbs in verse 10 are plural, and the second half of the verse addresses a world of nations. The most natural reading is that the ones told to drop their hands are the combatants, not the congregation — an order to the raging kingdoms of the previous verses.",
    },
    {
      type: "p",
      text: "That reading also explains why the verse ends where it does. I will be exalted among the nations is a strange thing to say to somebody trying to have a peaceful morning, and an entirely natural thing to say to armies.",
    },
  ],
  misreading: {
    claim:
      "That it is an invitation to be quiet and rest — the Bible's endorsement of stillness as a practice, suitable for a candle, a retreat or the start of a quiet time.",
    correction: [
      "The reading is understandable. In English, be still sounds like an invitation to a state of mind, and the verse arrives in most people's lives already detached from its psalm, printed on something. Read that way it is gentle, and it asks the reader to produce a feeling.",
      "The psalm asks for something else, and it is easier. The verb is closer to let go or cease; the addressee is the nations who have just had their weapons destroyed; the stillness follows from someone else having ended the fight. Nobody in the psalm is being asked to feel calm. They are being told to stop, because the outcome is no longer theirs to secure.",
      "That is a better verse to have at two in the morning than the cushion version. Told to be calm, you will fail and add the failure to the pile. Told to drop your hands because the thing is being handled elsewhere, there is something you can actually do.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read the whole psalm before the verse. It takes forty seconds, and verses 1 to 3 supply the setting the line was written into — refuge, and an earth that is coming apart at the same time.",
    },
    {
      type: "p",
      text: "Then use it as it is written: as permission to stop working on something, rather than as an instruction to feel peaceful. The psalm nearest to it in what it does is {Psalm-91}, which makes the same move — shelter inside the danger rather than the danger removed.",
      links: { "Psalm-91": "/verse/psalm-91" },
    },
    {
      type: "note",
      text: "The psalm has one more feature worth noticing, and it is structural. The refrain in verses 7 and 11 — Yahweh of Armies is with us, the God of Jacob is our refuge — arrives twice, and the second time is immediately after verse 10. The stillness is bracketed by a claim about company rather than about calm, which is the same shape the rest of the psalter uses when it is being useful.",
    },
    {
      type: "note",
      text: "A test for whether you have the right reading: the cushion version cannot be said in a hospital corridor. This one can.",
    },
  ],
  faq: [
    {
      q: "What does be still and know that I am God mean?",
      a: "In the psalm it means stop — let go, drop your hands — and it is addressed to nations at war whose weapons have just been destroyed. The stillness follows from the fight being ended by someone else, rather than being a state of mind to produce.",
    },
    {
      q: "Is Psalm 46:10 about meditation?",
      a: "Not in its own setting. The psalm is about the earth changing, waters roaring and kingdoms tottering, and verse 9 has God breaking bows and burning chariots. It can be used in quiet prayer, but the comfort it offers is that someone else is holding the situation.",
    },
    {
      q: "What is the Hebrew word in Psalm 46:10?",
      a: "A form of a root meaning to sink, slacken or let drop — used elsewhere of hands going limp and of leaving something alone. Several translations render it as enough! or stop!",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the verse is printed with the two lines that follow it rather than on its own. Its 'Be still, and know that I am God' is the familiar wording; what the popular reading leaves out is the psalm around it.",
    },
  ],
  related: ["how-to-stop-scrolling-at-night", "digital-sabbath"],
  relatedTopics: ["bible-verses-about-peace", "bible-verses-about-rest"],
  relatedPassages: ["psalm-91", "philippians-4-6-7"],
};
